import { ChevronDown, Filter, X } from 'lucide-react';
import { useState, useEffect } from 'react';

interface FilterPanelProps {
  onFilterChange: (filters: FilterState) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export interface FilterState {
  priceMin?: number;
  priceMax?: number;
  sources: string[];
  pickupDistance?: number;
  useAi?: boolean;
  // Query relevance filtering options
  enableQueryFilter?: boolean;
  minRelevanceScore?: number;
}

export function FilterPanel({ onFilterChange, isOpen, onToggle }: FilterPanelProps) {
  // Load saved preferences from localStorage
  const loadPreference = <T,>(key: string, defaultValue: T): T => {
    try {
      const saved = localStorage.getItem(`dryv_filter_${key}`);
      return saved !== null ? JSON.parse(saved) : defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const [priceMin, setPriceMin] = useState<string>('');
  const [priceMax, setPriceMax] = useState<string>('');
  const [sources, setSources] = useState<string[]>([]);
  const [pickupDistance, setPickupDistance] = useState<string>('');
  const [useAi, setUseAi] = useState<boolean>(false);
  const [enableQueryFilter, setEnableQueryFilter] = useState<boolean>(loadPreference('enableQueryFilter', true));
  const [minRelevanceScore, setMinRelevanceScore] = useState<number>(loadPreference('minRelevanceScore', 0.3));

  const emitChanges = (updatedSources?: string[]) => {
    const filterState = {
      priceMin: priceMin ? parseInt(priceMin) : undefined,
      priceMax: priceMax ? parseInt(priceMax) : undefined,
      sources: updatedSources || sources,
      pickupDistance: pickupDistance ? parseInt(pickupDistance) : undefined,
      useAi: useAi || undefined,
      enableQueryFilter: enableQueryFilter,
      minRelevanceScore: minRelevanceScore,
    };
    
    // Save preferences to localStorage
    localStorage.setItem('dryv_filter_enableQueryFilter', JSON.stringify(enableQueryFilter));
    localStorage.setItem('dryv_filter_minRelevanceScore', JSON.stringify(minRelevanceScore));
    
    onFilterChange(filterState);
  };

  const handlePriceChange = () => {
    emitChanges();
  };

  const handleSourceToggle = (sourceId: string) => {
    const updatedSources = sources.includes(sourceId)
      ? sources.filter((s) => s !== sourceId)
      : [...sources, sourceId];
    setSources(updatedSources);
    emitChanges(updatedSources);
  };

  // Emit initial filter state on mount to ensure preferences are applied
  useEffect(() => {
    emitChanges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handleReset = () => {
    setPriceMin('');
    setPriceMax('');
    setSources([]);
    setPickupDistance('');
    setUseAi(false);
    setEnableQueryFilter(true);
    setMinRelevanceScore(0.3);
    // Clear saved preferences
    localStorage.removeItem('dryv_filter_enableQueryFilter');
    localStorage.removeItem('dryv_filter_minRelevanceScore');
    onFilterChange({
      sources: [],
      enableQueryFilter: true,
      minRelevanceScore: 0.3,
    });
  };

  const hasActiveFilters = priceMin || priceMax || sources.length > 0 || pickupDistance || useAi || !enableQueryFilter || minRelevanceScore !== 0.3;

  return (
    <div className="card overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 sm:px-5 py-3 sm:py-4 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200 group"
        aria-expanded={isOpen}
        aria-controls="filter-panel-content"
        aria-label={isOpen ? 'Close filters' : 'Open filters'}
      >
          <div className="flex items-center gap-2 sm:gap-3">
          <Filter size={16} className="sm:w-[18px] sm:h-[18px] text-neutral-500 dark:text-neutral-400 group-hover:text-accent-600 dark:group-hover:text-accent-400 transition-colors" aria-hidden="true" />
          <span className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm sm:text-base">Filters</span>
          {hasActiveFilters && (
            <span className="px-1.5 sm:px-2 py-0.5 bg-accent-600 text-white text-[10px] sm:text-xs font-bold rounded-full">
              {[priceMin, priceMax, ...sources, pickupDistance, useAi].filter(Boolean).length}
            </span>
          )}
        </div>
        <ChevronDown
          size={18}
          className={`text-neutral-400 dark:text-neutral-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>

      {isOpen && (
        <div id="filter-panel-content" className="border-t border-neutral-200 dark:border-neutral-700 p-4 sm:p-5 space-y-4 sm:space-y-6 animate-slide-up" role="region" aria-label="Filter options">
          {/* Price Range */}
          <div className="space-y-2 sm:space-y-3">
            <label className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Price Range</label>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <label htmlFor="price-min" className="sr-only">Minimum price</label>
                <input
                  id="price-min"
                  type="number"
                  placeholder="Min"
                  value={priceMin}
                  onChange={(e) => {
                    setPriceMin(e.target.value);
                    handlePriceChange();
                  }}
                  className="input text-sm sm:text-base"
                  aria-label="Minimum price"
                />
              </div>
              <div>
                <label htmlFor="price-max" className="sr-only">Maximum price</label>
                <input
                  id="price-max"
                  type="number"
                  placeholder="Max"
                  value={priceMax}
                  onChange={(e) => {
                    setPriceMax(e.target.value);
                    handlePriceChange();
                  }}
                  className="input text-sm sm:text-base"
                  aria-label="Maximum price"
                />
              </div>
            </div>
          </div>

          {/* Marketplaces */}
          <div className="space-y-2 sm:space-y-3">
            <label className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">Marketplaces</label>
            <div className="space-y-2">
              {[
                { id: 'facebook_marketplace', label: 'Facebook Marketplace' },
                { id: 'offerup', label: 'OfferUp' },
                { id: 'amazon', label: 'Amazon' },
                { id: 'craigslist', label: 'Craigslist' },
                { id: 'ebay', label: 'eBay' },
              ].map((source) => (
                <label
                  key={source.id}
                  htmlFor={`source-${source.id}`}
                  className="flex items-center gap-2 sm:gap-3 cursor-pointer group p-2 sm:p-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200"
                >
                  <input
                    id={`source-${source.id}`}
                    type="checkbox"
                    checked={sources.includes(source.id)}
                    onChange={() => handleSourceToggle(source.id)}
                    className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-accent-600 focus:ring-2 focus:ring-accent-500 focus:ring-offset-0 cursor-pointer bg-white dark:bg-neutral-800"
                    aria-label={`Filter by ${source.label}`}
                  />
                  <span className="text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 font-medium group-hover:text-neutral-900 dark:group-hover:text-neutral-100">{source.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* AI-Powered Search */}
          <div className="space-y-2 sm:space-y-3">
            <label htmlFor="use-ai" className="flex items-center gap-2 sm:gap-3 cursor-pointer group p-2 sm:p-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200">
              <input
                id="use-ai"
                type="checkbox"
                checked={useAi}
                onChange={(e) => {
                  setUseAi(e.target.checked);
                  emitChanges();
                }}
                className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-accent-600 focus:ring-2 focus:ring-accent-500 focus:ring-offset-0 cursor-pointer bg-white dark:bg-neutral-800"
                aria-label="Enable AI-powered price analysis"
              />
              <div className="flex-1">
                <span className="text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 font-medium group-hover:text-neutral-900 dark:group-hover:text-neutral-100">AI-Powered Price Analysis</span>
                <p className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Find the best deals using AI</p>
              </div>
            </label>
          </div>

          {/* Pickup Distance */}
          <div className="space-y-2 sm:space-y-3">
            <label htmlFor="distance" className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
              Pickup Distance (miles)
            </label>
            <input
              id="distance"
              type="number"
              placeholder="Enter miles"
              value={pickupDistance}
              onChange={(e) => {
                setPickupDistance(e.target.value);
                handlePriceChange();
              }}
              className="input text-sm sm:text-base"
            />
          </div>

          {/* Query Relevance Filtering */}
          <div className="space-y-3 sm:space-y-4 border-t border-neutral-200 dark:border-neutral-700 pt-4 sm:pt-5">
            <div className="space-y-2 sm:space-y-3">
              <label htmlFor="enable-query-filter" className="flex items-center gap-2 sm:gap-3 cursor-pointer group p-2 sm:p-2.5 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-700 transition-colors duration-200">
                <input
                  id="enable-query-filter"
                  type="checkbox"
                  checked={enableQueryFilter}
                  onChange={(e) => {
                    setEnableQueryFilter(e.target.checked);
                    emitChanges();
                  }}
                  className="w-4 h-4 rounded border-neutral-300 dark:border-neutral-600 text-accent-600 focus:ring-2 focus:ring-accent-500 focus:ring-offset-0 cursor-pointer bg-white dark:bg-neutral-800"
                  aria-label="Enable query relevance filtering"
                />
                <div className="flex-1">
                  <span className="text-xs sm:text-sm text-neutral-700 dark:text-neutral-300 font-medium group-hover:text-neutral-900 dark:group-hover:text-neutral-100">Enable Query Relevance Filtering</span>
                  <p className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">Filter out irrelevant search results</p>
                </div>
              </label>
            </div>

            {enableQueryFilter && (
              <div className="space-y-2 sm:space-y-3 pl-6 sm:pl-8">
                <div className="flex items-center justify-between">
                  <label htmlFor="relevanceScore" className="block text-xs font-semibold text-neutral-700 dark:text-neutral-300 uppercase tracking-wider">
                    Minimum Relevance Score
                  </label>
                  <span className="text-xs sm:text-sm font-medium text-accent-600 dark:text-accent-400">
                    {(minRelevanceScore * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="space-y-2">
                  <input
                    id="relevanceScore"
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={minRelevanceScore}
                    onChange={(e) => {
                      const newValue = parseFloat(e.target.value);
                      setMinRelevanceScore(newValue);
                      emitChanges();
                    }}
                    className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-accent-600"
                  />
                  <div className="flex justify-between text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400">
                    <span>Lenient (0%)</span>
                    <span>Strict (100%)</span>
                  </div>
                  <p className="text-[10px] sm:text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {minRelevanceScore < 0.2 && 'Shows most results, including loosely related items'}
                    {minRelevanceScore >= 0.2 && minRelevanceScore < 0.4 && 'Balanced filtering, shows relevant results'}
                    {minRelevanceScore >= 0.4 && minRelevanceScore < 0.6 && 'Strict filtering, only highly relevant results'}
                    {minRelevanceScore >= 0.6 && 'Very strict, only exact or near-exact matches'}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Reset Button */}
          {hasActiveFilters && (
            <button
              onClick={handleReset}
              className="w-full btn-secondary py-2 sm:py-2.5 text-xs sm:text-sm flex items-center justify-center gap-2"
              aria-label="Reset all filters"
            >
              <X size={14} className="sm:w-4 sm:h-4" aria-hidden="true" />
              Reset Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
